import React, { useState, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password"; // Para campo de senha
import { Checkbox } from "primereact/checkbox"; // Para checkboxes
import { confirmDialog } from "primereact/confirmdialog";
import { ConfirmDialog } from "primereact/confirmdialog"; // For <ConfirmDialog /> component
import {jwtDecode} from "jwt-decode"; // Para decodificar o token JWT
import {
  useNotification
} from "context/NotificationContext"; // Importa o Provider e o hook
import { useLanguage } from "context/LanguageContext";

const UserManagement = () => {
  const { GetTranslation } = useLanguage();

  const [users, setUsers] = useState([]);
  const showNotification = useNotification(); 
  const [institutions, setInstitutions] = useState([]); // Para carregar instituições associadas do token
  const [newUserDialog, setNewUserDialog] = useState(false);
  const [editUserDialog, setEditUserDialog] = useState(false);
  const [selectedInstitutions, setSelectedInstitutions] = useState([]);
  const [user, setUser] = useState({
    id: null,
    name: "",
    email: "",
    is_deleted: "",
    institutions: [],
    password: "",
    confirmPassword: ""
  }); // Inclui instituições e senhas
  const [isEdit, setIsEdit] = useState(false);
  const [passwordError, setPasswordError] = useState(""); // Para exibir erro se as senhas não coincidirem

  // Função para buscar usuários novamente do backend
  const fetchUsers = () => {
    const token = localStorage.getItem("token");

    fetch(`${process.env.REACT_APP_API_URL}/user/users`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((response) => response.json())
      .then((data) => setUsers(data))
      .catch((error) => console.error("Erro ao carregar usuários:", error));
  };

  useEffect(() => {
    const token = localStorage.getItem("token"); // Fetch token from local storage
    const decodedToken = jwtDecode(token); // Decodificar o token JWT para pegar instituições

    // Carregar instituições do token JWT
    setInstitutions(decodedToken.sub.institutions);

    // Fetch users from backend API
    fetchUsers()
  }, []);

  // Abrir o diálogo para criar um novo usuário
  const OpenNewUserDialog = () => {
    setUser({
      id: null,
      name: "",
      email: "",
      institutions: [], // Resetar
      password: "",
      confirmPassword: ""
    });
    setSelectedInstitutions([]); // Resetar instituições selecionadas
    setPasswordError(""); // Resetar o erro de senha
    setIsEdit(false);
    setNewUserDialog(true);
  };

  // Abrir o diálogo para editar um usuário existente
  const OpenEditUserDialog = (userToEdit) => {
    setUser({
      ...userToEdit,
      password: "",
      confirmPassword: ""
    });
    setSelectedInstitutions(userToEdit.institutions); // Preencher com as instituições do usuário
    setPasswordError("");
    setIsEdit(true);
    setEditUserDialog(true);
  };

  const toggleUserStatus = (userToToggle) => {
    confirmDialog({
      message: `${GetTranslation('ConfirmAction')} ${userToToggle.is_deleted ? GetTranslation('Activate') : GetTranslation('Delete')} ${GetTranslation('User')} ${userToToggle.name}?`,
      header: GetTranslation('Confirmation'),
      icon: "pi pi-exclamation-triangle",
      accept: () => {
        const token = localStorage.getItem("token");
        const endpoint = userToToggle.is_deleted
          ? `${process.env.REACT_APP_API_URL}/user/activate_user`
          : `${process.env.REACT_APP_API_URL}/user/delete_user`;
  
        fetch(endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ user_id: userToToggle.id })
        })
          .then((response) => {
            if (!response.ok) {
              return response.json().then((errorData) => {
                showNotification({
                  type: "error",
                  message: errorData.error || "Erro ao alterar status do usuário",
                });
                throw new Error("Erro na requisição");
              });
            } else {
              showNotification({
                type: "success",
                message: `Usuário ${userToToggle.is_deleted ? 'ativado' : 'excluído'} com sucesso`,
              });
            }
            return response.json();
          })
          .then(() => fetchUsers()) // Recarregar a lista de usuários
          .catch((error) => console.error("Erro ao alterar status do usuário:", error));
      },
      acceptLabel: userToToggle.is_deleted ? "Ativar" : "Excluir",
      rejectLabel: "Cancelar"
    });
  };
  
  // Função para salvar um novo usuário ou atualizar
  const SaveNewUser = () => {
    const token = localStorage.getItem("token");

    // Verifica se as senhas coincidem
    if (user.password !== user.confirmPassword) {
      setPasswordError("As senhas não coincidem.");
      return;
    }

    // Filtrar instituições válidas (com institution_id)
    const validInstitutions = selectedInstitutions.filter((inst) => inst && inst.id);
    if (validInstitutions.length === 0) {
      showNotification({
        type: "error",
        message: "Selecione pelo menos uma instituição válida.",
      });
      return;
    }

    const dataToSend = {
      user_id: user.id,
      name: user.name,
      email: user.email,
      institutions: validInstitutions.map((inst) => inst.id), // Enviar IDs válidos das instituições
      password: user.password ? user.password : undefined,
    };

    const endpoint = isEdit
      ? `${process.env.REACT_APP_API_URL}/user/update_user_list`
      : `${process.env.REACT_APP_API_URL}/user/register_user_new`;

    fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`, // Inclua o token no cabeçalho Authorization
      },
      body: JSON.stringify(dataToSend),
    })
      .then((response) => {
        if (!response.ok) {
          response.json().then((errorData) => {
            showNotification({
              type: "error",
              message: errorData.error || GetTranslation("UserSavedSuccessfully"),
            });
          });
          throw new Error("Erro na requisição");
        } else {
          showNotification({
            type: "success",
            message: GetTranslation("UserSavedSuccessfully"),
          });
        }
        return response.json();
      })
      .then((data) => {
        setNewUserDialog(false);
        setEditUserDialog(false);
        fetchUsers(); // Recarregar a lista de usuários após a atualização ou criação
      })
      .catch((error) => console.error("Erro ao salvar usuário:", error));
  };

  // Renderiza os botões de ação (editar/excluir)
  const ActionTemplate = (rowData) => {
    return (
      <div className="d-flex flex-row flex-wrap gap-3">
        <Button
          icon="pi pi-pencil"
          className="p-button-rounded p-button-success mr-2"
          onClick={() => OpenEditUserDialog(rowData)}
        />
        <Button
          icon={rowData.is_deleted ? "pi pi-check" : "pi pi-trash"}
          className={rowData.is_deleted ? "p-button-rounded p-button-info" : "p-button-rounded p-button-danger"}
          onClick={() => toggleUserStatus(rowData)}
        />
      </div>
    );
  };

  const InstitutionsTemplate = (rowData) => {
    // Garante que institutions seja sempre uma array
    const institutionsArray = Array.isArray(rowData.institutions) ? rowData.institutions : [];
    return institutionsArray.length > 0
      ? institutionsArray.map((inst) => inst.institution_name).join(", ")
      : "Sem instituições"; // Ou retorne uma string vazia, dependendo da sua preferência
  };
  

  // Renderização dos diálogos de novo e edição de usuário
  const RenderFooter = () => {
    return (
      <div className="d-flex flex-row justify-content-between flex-wrap gap-3">
        <Button
          label={GetTranslation("Cancel")}
          icon="pi pi-times"
          severity="secondary"
          onClick={() => {
            setNewUserDialog(false);
            setEditUserDialog(false);
          }}
        />
        <Button
          label={GetTranslation("Salvar")}
          icon="pi pi-check"
          severity="success"
          onClick={SaveNewUser}
          autoFocus
        />
      </div>
    );
  };

  return (
    <div className="card p-3">
      <div className="d-flex flex-row justify-content-between flex-wrap gap-3 mb-3">
      <h2 className="m-0">{GetTranslation("UserManagement")}</h2>
      <Button
          label={GetTranslation("NewUser")}
          icon="pi pi-plus"
          onClick={OpenNewUserDialog}
          className="p-button-success mb-2"
        />
      </div>
      <ConfirmDialog />
      <DataTable value={users} responsiveLayout="scroll">
        <Column field="id" header={GetTranslation("ID")} sortable></Column>
        <Column field="name" header={GetTranslation("Name")} sortable></Column>
        <Column field="email" header={GetTranslation("Email")} sortable></Column>
        <Column field="is_deleted" header={GetTranslation("Deleted")} body={(rowData) => rowData.is_deleted ? GetTranslation("Yes") : GetTranslation("No")} sortable></Column>
        <Column field="institutions" header={GetTranslation("Institutions")} body={InstitutionsTemplate}></Column>
        <Column header={GetTranslation("Actions")} body={ActionTemplate}></Column>
      </DataTable>

      {/* Diálogo para Novo Usuário / Editar Usuário */}
      <Dialog
        header={isEdit ? GetTranslation("EditUser") : GetTranslation("NewUser")}
        visible={newUserDialog || editUserDialog}
        style={{ width: "30vw" }}
        footer={RenderFooter()}
        onHide={() => {
          setNewUserDialog(false);
          setEditUserDialog(false);
        }}
      >
        <div className="d-flex flex-column gap-3" style={{ width: "100%" }}>
          <div className="d-flex flex-column">
            <label htmlFor="name">{GetTranslation("Name")}</label>
            <InputText
              id="name"
              value={user.name}
              onChange={(e) => setUser({ ...user, name: e.target.value })}
            />
          </div>
          <div className="d-flex flex-column">
            <label htmlFor="email">{GetTranslation("Email")}</label>
            <InputText
              id="email"
              value={user.email}
              onChange={(e) => setUser({ ...user, email: e.target.value })}
            />
          </div>
          <div className="d-flex flex-column">
            <label htmlFor="institutions">{GetTranslation("Hospital")}</label>
            {institutions.map((inst) => (
              <div key={inst.id} className="d-flex align-items-center">
                <Checkbox
                  inputId={`institution-${inst.id}`}
                  checked={selectedInstitutions.some((i) => i.id === inst.id)}
                  onChange={(e) => {
                    let updatedInstitutions;
                    if (e.checked) {
                      updatedInstitutions = [...selectedInstitutions, { id: inst.id, name: inst.name }];
                    } else {
                      updatedInstitutions = selectedInstitutions.filter((i) => i.id !== inst.id);
                    }
                    setSelectedInstitutions(updatedInstitutions); // Atualizar o estado de instituições selecionadas
                  }}
                />
                <label
                  htmlFor={`institution-${inst.id}`}
                  className="ml-2"
                  style={{ marginLeft: "10px" }}
                >
                  {inst.name}
                </label>
              </div>
            ))}
          </div>
          <div className="d-flex flex-column">
            <label htmlFor="password">{GetTranslation("Password")}</label>
            <Password
              id="password"
              value={user.password}
              onChange={(e) => setUser({ ...user, password: e.target.value })}
              toggleMask
            />
          </div>
          <div className="d-flex flex-column">
            <label htmlFor="confirmPassword">{GetTranslation("ConfirmPassword")}</label>
            <Password
              id="confirmPassword"
              value={user.confirmPassword}
              onChange={(e) =>
                setUser({ ...user, confirmPassword: e.target.value })
              }
              toggleMask
            />
          </div>
          {passwordError && <div style={{ color: "red" }}>{passwordError}</div>}
        </div>
      </Dialog>
    </div>
  );
};

export default UserManagement;
